import { logger } from "@/lib/logger";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X } from "lucide-react";

const STORAGE_KEY = "ptc_cookie_consent";

type ConsentState = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp?: string;
};

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setVisible(true);
        return;
      }
      
      // Parse the consent data
      const consent: ConsentState = JSON.parse(raw);
      
      // Check if 90 days have passed since consent was given
      if (consent.timestamp) {
        const consentDate = new Date(consent.timestamp);
        const now = new Date();
        const daysSinceConsent = (now.getTime() - consentDate.getTime()) / (1000 * 60 * 60 * 24);
        
        if (daysSinceConsent >= 90) {
          // Consent expired after 90 days, clear it and show banner again
          localStorage.removeItem(STORAGE_KEY);
          setVisible(true);
          logger.info("CookieConsent: expired after 90 days, requesting new consent");
          return;
        }
      }
      
      // Valid consent exists and hasn't expired
      setVisible(false);
    } catch (e) {
      logger.warn("CookieConsent: localStorage not available or invalid data", e);
      // Still show to comply, but won't persist
      setVisible(true);
    }
  }, []);

  const saveConsent = (consent: ConsentState) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...consent, timestamp: new Date().toISOString() })
      );
    } catch (e) {
      logger.warn("CookieConsent: failed to persist", e);
    }
  };

  const acceptAll = () => {
    saveConsent({ essential: true, analytics: true, marketing: true });
    setVisible(false);
    logger.info("CookieConsent: accepted all");
  };

  const rejectNonEssential = () => {
    saveConsent({ essential: true, analytics: false, marketing: false });
    setVisible(false);
    logger.info("CookieConsent: rejected non-essential");
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-16 md:bottom-0 z-40 px-3 pb-3 md:px-4 md:pb-4">
      <Card className="mx-auto max-w-3xl border shadow-lg bg-background/98 backdrop-blur-sm">
        <div className="p-3 md:p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-sm md:text-base font-semibold mb-1">Cookie Preferences</h2>
              <p className="text-xs md:text-sm text-muted-foreground line-clamp-2 md:line-clamp-none">
                We use cookies to improve your experience. Accept all or manage your preferences.
              </p>
              <div className="mt-2 md:mt-3 flex flex-row gap-2">
                <Button size="sm" onClick={acceptAll} className="flex-1 md:flex-none text-xs md:text-sm h-8">
                  Accept all
                </Button>
                <Button size="sm" variant="outline" onClick={rejectNonEssential} className="flex-1 md:flex-none text-xs md:text-sm h-8">
                  Essential only
                </Button>
              </div>
            </div>
            <button
              aria-label="Close cookie banner"
              className="p-1.5 rounded-md hover:bg-muted text-muted-foreground shrink-0"
              onClick={rejectNonEssential}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}
